import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { api } from "../lib/api";
import { CATEGORIES } from "../context/SettingsContext";

export default function Shop({ sale = false }) {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const tag = searchParams.get("tag");
  const [products, setProducts] = useState(null);

  const title = useMemo(() => {
    if (sale) return "SALE";
    if (tag === "new-drop") return "NEW DROP";
    if (tag === "best-seller") return "BEST SELLER";
    if (category) {
      const c = CATEGORIES.find((x) => x.slug === category);
      return c ? c.name : "SHOP";
    }
    return "SHOP ALL";
  }, [tag, category, sale]);

  useEffect(() => {
    setProducts(null);
    const params = {};
    if (tag) params.tag = tag;
    if (category) params.category = category;
    if (sale) params.sale = true;
    const load = () => api.getProducts(params).then(setProducts);
    load();
    window.addEventListener("focus", load);
    return () => window.removeEventListener("focus", load);
  }, [tag, category, sale]);

  return (
    <div className="fade-up">
      <div className="border-b border-neutral-200">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-10 text-center">
          <p className="text-xs tracking-[0.25em] text-neutral-400">
            <Link to="/" className="hover:text-black">HOME</Link> / {title}
          </p>
          <h1 className="font-display text-4xl md:text-5xl tracking-wide mt-3">{title}</h1>
          {products && <p className="text-sm text-neutral-500 mt-2">{products.length} products</p>}
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 md:px-8 pt-8">
        <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
          <Link
            to="/shop"
            className={`px-4 py-2 text-xs tracking-[0.2em] border transition-colors ${
              !category && !tag ? "bg-black text-white border-black" : "border-neutral-300 text-neutral-600 hover:border-black"
            }`}
          >
            ALL
          </Link>
          {CATEGORIES.map((c) => (
            <Link
              key={c.slug}
              to={`/shop/${c.slug}`}
              className={`px-4 py-2 text-xs tracking-[0.2em] border transition-colors ${
                category === c.slug ? "bg-black text-white border-black" : "border-neutral-300 text-neutral-600 hover:border-black"
              }`}
            >
              {c.name}
            </Link>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-12">
        {!products ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-10">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[3/4] bg-neutral-100" />
                <div className="h-3 bg-neutral-100 mt-3 w-3/4 mx-auto" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <p className="text-center text-neutral-500 py-20">No products found in this collection.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-10">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}
